/**
 * Recording Play Cell (Story 22.5 — Task 11 / AC #9)
 *
 * Per-row ▶ Play button for the CallRail / CTM Calls-List data-table widgets.
 *
 * ## Behavior
 *
 *   - On click: POSTs to `/api/recordings/issue` with
 *     `{ integrationId, recordingId }` (or the portal-view variant in the
 *     portal viewer flow).
 *   - On success: reveals an inline `<audio controls>` element below the
 *     row. The element auto-plays.
 *   - On error: shows a destructive-colored ✕ + a brief inline message.
 *   - Single-playback invariant: clicking Play on a second row pauses
 *     every other audio element in the registry first. The registry is a
 *     module-level `Set<HTMLAudioElement>` shared across all instances.
 *
 * ## Recordings-enabled gate
 *
 *   The Play button itself is rendered conditionally by the parent
 *   DataTable widget — when `recordingColumn` config is absent OR when
 *   the row's `_recordingsEnabled === false` metadata flag is set, the
 *   cell renders "🔇 Recording disabled" instead.
 *
 * ## Variant Detection
 *
 *   - Authenticated agency users: POST `/api/recordings/issue`
 *   - Portal viewers: POST `/api/portal-view/:portalToken/recordings/issue`
 *
 *   The component reads `portalToken` from the optional prop (provided by
 *   the parent widget when rendering inside the public portal layout).
 *
 * ## Single-playback registry
 *
 *   We use a module-scoped `Set<HTMLAudioElement>` because the
 *   DataTableWidget is the parent across all calls-list widgets in a
 *   single report — clicking row N in widget A must pause row M in
 *   widget B too. A React context would work, but the Set is simpler
 *   and the playback contract is intentionally global.
 */

import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Play as PlayIcon, X as XIcon, CircleNotch } from "@phosphor-icons/react";

// ============================================================================
// Module-scoped single-playback registry
// ============================================================================

const ACTIVE_PLAYERS = new Set<HTMLAudioElement>();

function pauseAllExcept(except: HTMLAudioElement | null): void {
  for (const el of ACTIVE_PLAYERS) {
    if (el !== except) {
      try {
        el.pause();
      } catch {
        // ignore — element may have been removed by React
      }
    }
  }
}

// ============================================================================
// Types
// ============================================================================

export interface RecordingPlayCellProps {
  /** The integration's UUID. */
  readonly integrationId: string;
  /** Per-call recording ID (CallRail `id` or CTM numeric string). */
  readonly recordingId: string;
  /**
   * When rendering inside the public client portal, pass the portal token
   * here. The component routes to the portal-view variant of the issue
   * endpoint and supplies the cookie credential automatically (it's
   * already set on `document.cookie`).
   */
  readonly portalToken?: string;
}

interface IssueResponse {
  url: string;
  expiresAt: number;
}

// ============================================================================
// Helpers
// ============================================================================

async function postIssueRecording(
  integrationId: string,
  recordingId: string,
  portalToken: string | undefined
): Promise<IssueResponse> {
  const path = portalToken
    ? `/api/portal-view/${encodeURIComponent(portalToken)}/recordings/issue`
    : `/api/recordings/issue`;
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ integrationId, recordingId }),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const code =
      (errorBody as { error?: { code?: string } })?.error?.code ??
      `HTTP_${response.status}`;
    throw new Error(code);
  }
  const json = (await response.json()) as { data?: IssueResponse };
  if (!json.data || typeof json.data.url !== "string") {
    throw new Error("MALFORMED_RESPONSE");
  }
  return json.data;
}

// ============================================================================
// Component
// ============================================================================

export function RecordingPlayCell({
  integrationId,
  recordingId,
  portalToken,
}: RecordingPlayCellProps): ReactElement {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; url: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Clean up the registry when this row's audio element unmounts (e.g.
  // re-render or row removal).
  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) ACTIVE_PLAYERS.delete(el);
    };
  }, []);

  const handlePlay = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { url } = await postIssueRecording(
        integrationId,
        recordingId,
        portalToken
      );
      setState({ kind: "ready", url });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "UNKNOWN_ERROR",
      });
    }
  }, [integrationId, recordingId, portalToken]);

  // When the audio element mounts, register it + pause others on play.
  const handleAudioRef = useCallback((el: HTMLAudioElement | null) => {
    if (el && audioRef.current !== el) {
      audioRef.current = el;
      ACTIVE_PLAYERS.add(el);
    }
  }, []);

  const handleAudioPlay = useCallback(() => {
    pauseAllExcept(audioRef.current);
  }, []);

  if (state.kind === "ready") {
    return (
      <span className="inline-flex items-center gap-2">
        <audio
          ref={handleAudioRef}
          controls
          autoPlay
          src={state.url}
          onPlay={handleAudioPlay}
          className="h-8 max-w-[260px]"
          data-testid="recording-audio-element"
        />
      </span>
    );
  }

  if (state.kind === "error") {
    return (
      <span
        className="inline-flex items-center gap-1 text-destructive text-xs"
        data-testid="recording-play-error"
        title={state.message}
      >
        <XIcon size={14} />
        Failed
      </span>
    );
  }

  const isLoading = state.kind === "loading";
  return (
    <button
      type="button"
      onClick={handlePlay}
      disabled={isLoading}
      data-testid="recording-play-button"
      className="inline-flex items-center gap-1 rounded-lg h-7 px-2 text-xs hover:bg-muted disabled:cursor-not-allowed cursor-pointer"
    >
      {isLoading ? (
        <CircleNotch size={14} className="animate-spin" />
      ) : (
        <PlayIcon size={14} weight="fill" />
      )}
      Play
    </button>
  );
}
